// import { useState } from 'react'
import { motion } from 'framer-motion'

/**
 * Footer - Minimalistic Luxury Footer Component
 * 
 * Features:
 * - Logo (40px height) with fade-in animation
 * - Email link with hover underline effect
 * - Social icons (LinkedIn, Behance, Instagram) with scale hover
 * - VIP microcopy: "If you felt it, you'll find us."
 * - No testimonials or project gallery (minimalistic approach)
 * 
 * Animation Details:
 * - Logo fades in with 0.8s delay
 * - Social icons scale 1.1 on hover (0.2s transition)
 * - Email underline animation on hover
 */

export default function Footer() {

  return (
    <footer className="py-16 bg-luxury-black border-t border-luxury-gold/10">
      <div className="container mx-auto px-6">
        {/* Centered Logo with Amazing Effects */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          viewport={{ once: true }}
          className="flex justify-center mb-12"
        >
          <div className="relative">
            {/* Sparkle Effects */}
            <motion.div
              className="absolute inset-0"
              animate={{
                opacity: [0, 1, 0]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              {[...Array(12)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2"
                  style={{
                    left: `${10 + (i * 7)}%`,
                    top: `${15 + (i * 6)}%`,
                  }}
                  animate={{
                    rotate: [0, 360],
                    scale: [0, 1, 0],
                    opacity: [0, 1, 0]
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.2
                  }}
                >
                  <div className="w-full h-full bg-gradient-to-r from-cyan-300 to-purple-400 rounded-full blur-sm" />
                </motion.div>
              ))}
            </motion.div>

            {/* Holographic Depth Effect */}
            <motion.div
              className="absolute inset-0 rounded-full"
              animate={{
                background: [
                  "radial-gradient(circle, rgba(34,211,238,0.1) 0%, transparent 70%)",
                  "radial-gradient(circle, rgba(147,51,234,0.1) 0%, transparent 70%)",
                  "radial-gradient(circle, rgba(236,72,153,0.1) 0%, transparent 70%)",
                  "radial-gradient(circle, rgba(34,211,238,0.1) 0%, transparent 70%)"
                ]
              }}
              transition={{
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />

            {/* Rotating Radiation Glowing Ring */}
            <motion.div
              className="absolute inset-0 rounded-full"
              animate={{
                boxShadow: [
                  "0 0 30px rgba(34, 211, 238, 0.4), 0 0 60px rgba(34, 211, 238, 0.3), 0 -15px 40px rgba(34, 211, 238, 0.6)",
                  "0 0 40px rgba(147, 51, 234, 0.5), 0 0 80px rgba(147, 51, 234, 0.4), 0 -10px 50px rgba(147, 51, 234, 0.7)",
                  "0 0 25px rgba(236, 72, 153, 0.3), 0 0 50px rgba(236, 72, 153, 0.2), 0 -20px 60px rgba(236, 72, 153, 0.5)",
                  "0 0 35px rgba(34, 211, 238, 0.6), 0 0 70px rgba(34, 211, 238, 0.5), 0 -8px 35px rgba(34, 211, 238, 0.8)"
                ]
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            
            {/* Color-Changing Border Ring */}
            <motion.div
              className="absolute inset-0 rounded-full border-2"
              animate={{
                opacity: [0.4, 0.8, 0.4],
                scale: [1, 1.05, 1],
                borderColor: [
                  "rgba(34, 211, 238, 1)",
                  "rgba(147, 51, 234, 1)",
                  "rgba(236, 72, 153, 1)",
                  "rgba(34, 211, 238, 1)"
                ],
                boxShadow: [
                  "0 0 8px rgba(34, 211, 238, 0.4)",
                  "0 0 12px rgba(147, 51, 234, 0.5)",
                  "0 0 8px rgba(236, 72, 153, 0.4)",
                  "0 0 10px rgba(34, 211, 238, 0.4)"
                ]
              }}
              transition={{
                duration: 12,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            
            {/* Main Logo with Enhanced Effects */}
            <motion.img
              src="/logo.png" 
              alt="MindGlow Logo" 
              className="w-24 h-24 md:w-32 md:h-32 object-contain relative z-10"
              animate={{
                filter: [
                  "drop-shadow(0 0 15px rgba(34, 211, 238, 0.4)) drop-shadow(0 0 25px rgba(255, 255, 255, 0.2)) drop-shadow(0 -8px 20px rgba(34, 211, 238, 0.5))",
                  "drop-shadow(0 0 20px rgba(147, 51, 234, 0.5)) drop-shadow(0 0 35px rgba(255, 255, 255, 0.3)) drop-shadow(0 -10px 25px rgba(147, 51, 234, 0.6))",
                  "drop-shadow(0 0 15px rgba(236, 72, 153, 0.4)) drop-shadow(0 0 25px rgba(255, 255, 255, 0.2)) drop-shadow(0 -8px 20px rgba(236, 72, 153, 0.5))"
                ],
                scale: [1, 1.02, 1, 1.01, 1]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              whileHover={{
                scale: 1.1,
                filter: "drop-shadow(0 0 80px rgba(34, 211, 238, 1.0)) drop-shadow(0 0 120px rgba(255, 255, 255, 0.5))",
                transition: { duration: 0.4, ease: "easeOut" }
              }}
              style={{
                zIndex: 1000,
                position: 'relative'
              }}
            />
          </div>
        </motion.div>

        {/* Contact & Social - Centered Below Logo */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="flex flex-col items-center space-y-6"
        >
          {/* Email */}
          <motion.a
            href="mailto:hello@mindglow.app"
            className="text-cyan-300 hover:text-white transition-colors duration-300 relative group text-lg font-medium"
            style={{
              fontFamily: "'Inter', 'Helvetica Neue', sans-serif"
            }}
            whileHover={{ scale: 1.05 }}
            animate={{
              textShadow: [
                "0 0 10px rgba(34, 211, 238, 0.3)",
                "0 0 20px rgba(34, 211, 238, 0.6)",
                "0 0 10px rgba(34, 211, 238, 0.3)"
              ]
            }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            hello@mindglow.app
            <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-cyan-300 transition-all duration-300 group-hover:w-full" />
          </motion.a>
          
        </motion.div>
        
        {/* VIP Microcopy */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 1 }}
          viewport={{ once: true }}
          className="text-center mt-8 pt-8 border-t border-luxury-gold/20"
        >
          <motion.p 
            className="text-lg md:text-xl text-cyan-300 font-medium italic"
            style={{
              fontFamily: "'Playfair Display', 'Cormorant Garamond', serif",
              fontWeight: "500",
              lineHeight: "1.4"
            }}
            animate={{
              textShadow: [
                "0 0 20px rgba(34, 211, 238, 0.4)",
                "0 0 30px rgba(34, 211, 238, 0.7)",
                "0 0 20px rgba(34, 211, 238, 0.4)"
              ]
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            If you felt it, you'll find us.
          </motion.p>
          
          {/* Animated Underline */}
          <motion.div
            className="mt-4 mx-auto h-0.5 bg-gradient-to-r from-transparent via-cyan-300 to-transparent"
            initial={{ width: 0 }}
            whileInView={{ width: "40%" }}
            transition={{ duration: 1.5, delay: 1.5 }}
          />
        </motion.div>
      </div>
    </footer>
  )
}
